import { FC, useState } from "react";
import { useForm } from "react-hook-form";
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import TelegramIcon from '@mui/icons-material/Telegram';
import { Box, TextField, Button, Typography, IconButton, Alert, Snackbar } from "@mui/material";
import { headerHeight } from "../../NavBars/Header/hardCodeData";
import contact_us from "../../Images/ContactUs/contact_us.jpg";
import { useTranslation } from "react-i18next";
import Viber from "../../IconsCVG/viber";
import { sendContactForm } from "../../api/contactApi";

type FormData = {
  name: string;
  phone: string;
  comment: string;
};

const ContactUs: FC = () => {
  const { t } = useTranslation();
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<FormData>({
    defaultValues: {
      name: "",
      phone: "",
      comment: "",
    },
  });

  const onSubmit = async (data: FormData) => {
    try {
      const result = await sendContactForm(data);

      if (result.success) {
        setNotification({
          open: true,
          message: result.message || t('forms:form_success'),
          severity: 'success',
        });
        reset();
      } else {
        setNotification({
          open: true,
          message: result.error || t('forms:form_error'),
          severity: 'error',
        });
      }
    } catch (error) {
      setNotification({
        open: true,
        message: t('forms:form_network_error'),
        severity: 'error',
      });
    }
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  return (
    <Box
      sx={{
        minHeight: `calc(100vh - ${headerHeight})`,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        fontSize: "2vw",
        fontWeight: 700,
      }}
    >
      <Box
        sx={{
          height: { xs: "50vw", md: `60vh` },
          width: { xs: "100vw", md: "calc(100vw - 15px)" },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          mt: headerHeight,
          backgroundImage: {
            xs: `url(${contact_us})`,
            md: `url(${contact_us})`,
          },
          backgroundSize: { xs: "100vw 50vw", md: "cover" },
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center center",
        }}
      >
        <Typography
          sx={{
            fontFamily: "Montserrat",
            fontSize: { xs: "9vw", md: "83px" },
            color: "#fff",
            fontWeight: 600,
            letterSpacing: "-3px",
            textTransform: "uppercase",
            mt: 5,
          }}
        >
          {t("common:contact_us")}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "center", md: "start" },
        }}
      >
        <Box
          sx={{
            width: { xs: "100%", md: "60%", lg: "30%" },
            display: "flex",
            justifyContent: "center",
            pt: 4,
          }}
        >
          <Box sx={{ width: { xs: "calc(100% - 10vw*2)", md: "300px" } }}>
            <Typography
              sx={{
                borderBottom: "1px solid #000",
                pb: 0,
                fontFamily: "Montserrat",
                fontWeight: 500,
                fontSize: "36px",
                textTransform: "capitalize",
                textAlign: { xs: "left", md: "center" },
              }}
            >
              {t("common:contacts")}
            </Typography>


            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: {
                  xs: "calc(75vw + 1.66vw)",
                  sm: "calc(250px * 1)",
                  md: "auto",
                },
                mt: { xs: "5vw", sm: 1 },
              }}
            >
              <IconButton
                component="a"
                href="tel:+37379200221"
                sx={{
                  px: { xs: 1.5, sm: 3 },
                  height: { xs: "9.5vw", sm: "50px" },
                  color: "#000",
                  fontSize: { xs: "3vw", sm: "18px" },
                  letterSpacing: { xs: 0, sm: "auto" },
                  fontFamily: "Montserrat",
                  fontWeight: 600,
                  textTransform: "none",
                  "&:hover": {
                    background: "rgba(0, 0, 0, 0.05)",
                    color: { xs: "#000", sm: "#000" },
                  },
                  width: "50px",
                }}
              >
                <LocalPhoneIcon sx={{ fontSize: { xs: "5.6vw", sm: "29px" } }} />
              </IconButton>
              <IconButton
                component="a"
                href="viber://chat?number=%2B37379200221"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  height: { xs: "9.5vw", sm: "50px" },
                  color: "#7765f2",
                  fontSize: { xs: "3vw", sm: "18px" },
                  transition: { xs: "none", sm: "ease-in-out .15s" },
                  "&:hover": {
                    background: "rgba(119, 101, 242, 0.1)",
                  },
                  width: "50px",
                }}
              >
                <Box
                  sx={{
                    height: { xs: "5.7vw", sm: "30.5px" },
                    width: { xs: "5.7vw", sm: "30.5px" },
                    color: "inherit",
                  }}
                >
                  <Viber />
                </Box>
              </IconButton>

              <IconButton
                component="a"
                href="https://wa.me/+37379200221"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  px: { xs: 1.5, sm: 3 },
                  height: { xs: "9.5vw", sm: "50px" },
                  color: "#2cd46b",
                  fontSize: { xs: "3vw", sm: "18px" },
                  transition: { xs: "none", sm: "ease-in-out 0.15s" },
                  "&:hover": {
                    background: "rgba(44, 212, 107, 0.1)",
                  },
                  width: "50px",
                }}
              >
                <WhatsAppIcon
                  sx={{ color: "inherit", fontSize: { xs: "7vw", sm: "36px" } }}
                />
              </IconButton>

              <IconButton
                component="a"
                href="https://t.me/+37379200221"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  px: { xs: 1.5, sm: 3 },
                  height: { xs: "9.5vw", sm: "50px" },
                  color: "#2fabea",
                  fontSize: { xs: "3vw", sm: "18px" },
                  transition: { xs: "none", sm: "ease-in-out 0.15s" },
                  "&:hover": {
                    background: "rgba(47, 175, 174, 0.1)",
                  },
                  width: "50px",
                }}
              >
                <TelegramIcon
                  sx={{ color: "#inherit", fontSize: { xs: "7vw", sm: "36px" } }}
                />
              </IconButton>
            </Box>




            <Typography
              sx={{ letterSpacing: "3px", fontFamily: "Montserrat", fontStyle: "italic", fontSize: { xs: "7vw", sm: "32px", md: "32px" }, fontWeight: 400, textAlign: { xs: "center", sm: "left" } }}
            >
              +373 79 200 221
            </Typography>

            {/* Address */}
            <Typography
              sx={{
                mt: 1,
                fontFamily: "Montserrat",
                fontSize: { xs: "4vw", sm: "18px", md: "18px" },
                fontWeight: 400,
                textAlign: { xs: "center", sm: "left" },
                color: "#666"
              }}
            >
              {t("forms:str")} Uzinelor 8/1A
            </Typography>

            {/* Google Maps */}
            <Box
              sx={{
                mt: 1,
                width: "100%",
                height: "250px",
                // borderRadius: "8px",
                overflow: "hidden",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
              }}
            >
              <iframe
                title="BYD Cars Service Location - Uzinelor 8/1A"
                src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d860.7432943322679!2d28.87250419502598!3d47.012815541050905!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e1!3m2!1sru!2s!4v1753444300216!5m2!1sru!2s"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </Box>

          </Box>
        </Box>
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            py: 10,
            px: "10vw",
            borderLeft: { xs: "none", md: "#a9a9a9 solid 1px" },
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography variant="h5">{t("forms:fill_out_this_form")}</Typography>

          <TextField
            label={t("forms:how_can_i_call_you")}
            variant="outlined"
            fullWidth
            {...register("name", {
              required: t("forms:enter_name"),
              minLength: {
                value: 2,
                message: t("forms:min_2_characters"),
              },
            })}
            error={!!errors.name}
            helperText={errors.name?.message}
            sx={{
              borderRadius: 0,
              "& .MuiOutlinedInput-root": { borderRadius: 0 },
            }}
          />

          <TextField
            label={t("forms:your_phone_number")}
            variant="outlined"
            fullWidth
            {...register("phone", {
              required: t("forms:enter_phone"),
              pattern: {
                value: /^\+?[0-9\s\-()]{3,20}$/,
                message: t("forms:invalid_phone_format"),
              },
            })}
            error={!!errors.phone}
            helperText={errors.phone?.message}
            sx={{
              borderRadius: 0,
              "& .MuiOutlinedInput-root": { borderRadius: 0 },
            }}
          />

          <TextField
            label={t("forms:message")}
            variant="outlined"
            fullWidth
            multiline
            rows={4}
            {...register("comment", {
              minLength: {
                value: 5,
                message: t("forms:min_5_characters"),
              },
            })}
            error={!!errors.comment}
            helperText={errors.comment?.message}
            sx={{
              borderRadius: 0,
              "& .MuiOutlinedInput-root": { borderRadius: 0 },
            }}
          />

          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            sx={{ borderRadius: 0, width: { xs: "200px", md: "250px" } }}
          >
            {isSubmitting ? t("forms:form_sending") : t("forms:send")}
          </Button>
        </Box>
      </Box>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box >
  );
};

export default ContactUs;
